import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../service/authService.service';
import { I18nService } from '../../service/i18n.service';
import { CabecalhoComponent } from '../cabecalho/cabecalho.component';
import { RodapeComponent } from '../rodape/rodape.component';

@Component({
  selector: 'app-index',
  standalone: true,
  imports: [CommonModule, TranslateModule, RodapeComponent, CabecalhoComponent],
  templateUrl: './index.component.html',
  styleUrl: './index.component.scss',
})
export class IndexComponent implements OnInit {
  translate = inject(TranslateService);
  i18nService = inject(I18nService);
  router = inject(Router);
  authService = inject(AuthService);

  // Signals para controle de estado
  isScrolled = signal(false);
  stats = signal([
    { number: 500, label: 'Agendamentos Processados', suffix: '+' },
    { number: 50, label: 'Clientes Satisfeitos', suffix: '+' },
    { number: 98, label: '% Taxa de Sucesso', suffix: '%' },
    { number: 24, label: 'Horas de Suporte', suffix: '/7' },
  ]);

  teamMembers = signal([
    {
      name: 'Marcelly Maia',
      role: 'Co-fundadora & Analista de dados',
      description:
        'Especialista em dados, desenvolve soluções inteligentes que transformam informações em insights valiosos.',
      initials: 'MM',
    },

    {
      name: 'Luccas Silva',
      role: 'Co-fundador & Devops Engineer',
      description:
        'Mestre em desenvolvimento de software, cuida da infraestrutura que suporta nossas soluções, garantindo performance e segurança excepcionais.',
      initials: 'LS',
    },
    {
      name: 'Patricia Froes',
      role: 'Co-fundadora & Product Manager',
      description:
        'Visionária de produtos, conecta necessidades do mercado com soluções tecnológicas, garantindo que cada feature agregue valor real.',
      initials: 'PF',
    },
    {
      name: 'Serafim Froes',
      role: 'Co-fundador & Frontend Developer',
      description:
        'Arquiteto de sistemas web, cria interfaces modernas e intuitivas, garantindo uma experiência de usuário excepcional.',
      initials: 'SF',
    },
  ]);

  constructor() {
    this.translate.use(this.i18nService.idioma.value.code);
  }

  ngOnInit() {
    // Scroll effect
    window.addEventListener('scroll', () => {
      this.isScrolled.set(window.scrollY > 100);
    });

    // Initialize animations and observers
    this.initializeAnimations();
  }

  navigateTo(): void {
    window.open(
      'https://api.whatsapp.com/send/?phone=553199179151&text=Ol%C3%A1%21+Visitei+seu+site+e+gostaria+de+conhecer+seus+servi%C3%A7os&type=phone_number&app_absent=0',
      '_blank'
    );
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToTrial(): void {
    this.navigateTo();
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private initializeAnimations(): void {
    // Intersection Observer para animações
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Aplicar observer aos elementos com fade-in
    setTimeout(() => {
      document.querySelectorAll('.fade-in').forEach((el) => {
        observer.observe(el);
      });
    }, 100);

    // Counter animation para estatísticas
    this.initializeCounters();
  }

  private initializeCounters(): void {
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const counter = entry.target as HTMLElement;
          const target = parseInt(counter.getAttribute('data-count') || '0');
          this.animateCounter(counter, target);
          counterObserver.unobserve(counter);
        }
      });
    });

    setTimeout(() => {
      document.querySelectorAll('.counter').forEach((counter) => {
        counterObserver.observe(counter);
      });
    }, 100);
  }

  private animateCounter(element: HTMLElement, target: number): void {
    let current = 0;
    const increment = target / 100;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        this.setCounterText(element, target);
        clearInterval(timer);
      } else {
        this.setCounterText(element, Math.floor(current));
      }
    }, 20);
  }

  private setCounterText(element: HTMLElement, value: number): void {
    const suffix = element.getAttribute('data-suffix') || '';
    if (suffix === '%') {
      element.textContent = value + '%';
    } else if (suffix === '/7') {
      element.textContent = value + '/7';
    } else {
      element.textContent = value + '+';
    }
  }
}
